import { useState, useCallback, useEffect, useMemo } from 'react';
import { Plus, ChevronRight } from 'lucide-react';
import type { EventMember, EventTable, SfdcUser } from '../../event-manager-client';
import { useSeatingChart, computeMetWith } from '../../hooks/useSeatingChart';
import { useCreateEventTable, usePatchEventTable, useDeleteEventTable } from '../../hooks/useEventTableMutation';
import { Modal } from '../../../_shared/components';
import { UnassignedPanel } from './UnassignedPanel';
import { TableCard } from './TableCard';
import { nextTableName } from './utils';
import { probeThenFanOut, isSalesforceReauthError, fanOutFailureMessage } from './probe-fan-out';

interface SeatingChartProps {
  campaignId: string;
  members: EventMember[];
  tables: EventTable[];
  sfdcUsers: SfdcUser[];
  onSelectMember: (member: EventMember) => void;
  onPatchMember: (memberId: number, fields: Record<string, unknown>) => Promise<void>;
  onAssignTable: (memberId: number, tableId: number | null, seatPosition?: number | null) => Promise<void>;
  showError: (msg: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function SeatingChart({
  campaignId, members, tables, sfdcUsers,
  onSelectMember, onPatchMember, onAssignTable, showError, onDirtyChange,
}: SeatingChartProps) {
  const {
    effectiveAssignments, tableMembers, unassignedMembers,
    moveMember, clearPending, clearPendingForTable, isDirty, pending, pendingChanges,
  } = useSeatingChart(members, tables);

  const createTable = useCreateEventTable(campaignId);
  const patchTable = usePatchEventTable(campaignId);
  const deleteTable = useDeleteEventTable(campaignId);
  const [saving, setSaving] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  // ── Confirmation modals ──────────────────────────────────────────────
  const [pendingDeleteTableId, setPendingDeleteTableId] = useState<number | null>(null);
  const [pendingUnassignMemberId, setPendingUnassignMemberId] = useState<number | null>(null);

  const pendingDeleteTable = pendingDeleteTableId != null
    ? tables.find(t => t.id === pendingDeleteTableId) ?? null
    : null;
  const pendingDeleteMembers = pendingDeleteTableId != null
    ? (tableMembers.get(pendingDeleteTableId) ?? [])
    : [];

  const pendingUnassignMember = pendingUnassignMemberId != null
    ? members.find(m => m.id === pendingUnassignMemberId) ?? null
    : null;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleAddTable = () => {
    createTable.mutate({
      table_name: nextTableName(tables),
      capacity: 6,
      sort_order: tables.length + 1,
    });
  };

  const handlePatchTable = useCallback((tableId: number, fields: Partial<{ table_name: string; capacity: number }>) => {
    patchTable.mutate({ tableId, fields });
  }, [patchTable]);

  const handleDeleteTable = useCallback((tableId: number) => {
    setPendingDeleteTableId(tableId);
  }, []);

  const confirmDeleteTable = useCallback(async () => {
    if (pendingDeleteTableId == null) return;
    const affectedMembers = tableMembers.get(pendingDeleteTableId) ?? [];

    if (affectedMembers.length > 0) {
      const clearFields = { primary_met_with: null, secondary_met_with: null, tertiary_met_with: null };
      const cleared = await probeThenFanOut(
        affectedMembers,
        m => onPatchMember(m.id, clearFields),
        isSalesforceReauthError,
      );
      const clearMessage = fanOutFailureMessage(cleared, 'met-with assignment');
      if (clearMessage) showError(clearMessage);
      if (!cleared.ok) return;
    }

    clearPendingForTable(pendingDeleteTableId);

    // Delete the table (API unassigns members server-side)
    deleteTable.mutate(pendingDeleteTableId);

    // Recalculate met-with for adjacent tables whose assignments may have changed
    const sortedTables = [...tables].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    const idx = sortedTables.findIndex(t => t.id === pendingDeleteTableId);
    if (idx >= 0) {
      const remainingTables = sortedTables.filter(t => t.id !== pendingDeleteTableId);
      const adjacentIds = new Set<number>();
      if (idx > 0) adjacentIds.add(sortedTables[idx - 1].id);
      if (idx < sortedTables.length - 1) adjacentIds.add(sortedTables[idx + 1].id);

      if (adjacentIds.size > 0) {
        // Recompute with the deleted table removed
        const updatedAssignments = new Map(effectiveAssignments);
        for (const m of affectedMembers) updatedAssignments.set(m.id, { tableId: null, seatPosition: null });

        const metWithChanges = computeMetWith(members, remainingTables, sfdcUsers, updatedAssignments);
        const adjacentChanges = Array.from(metWithChanges).filter(([memberId]) => {
          const a = updatedAssignments.get(memberId);
          return a?.tableId != null && adjacentIds.has(a.tableId);
        });

        if (adjacentChanges.length > 0) {
          const adjacent = await probeThenFanOut(
            adjacentChanges,
            ([memberId, fields]) => onPatchMember(memberId, fields),
            isSalesforceReauthError,
          );
          const adjacentMessage = fanOutFailureMessage(adjacent, 'met-with assignment');
          if (adjacentMessage) showError(adjacentMessage);
        }
      }
    }

    setPendingDeleteTableId(null);
  }, [pendingDeleteTableId, tableMembers, clearPendingForTable, onPatchMember, deleteTable, tables, effectiveAssignments, members, sfdcUsers, showError]);

  const handleUnassignDrop = useCallback((memberId: number) => {
    const member = members.find(m => m.id === memberId);
    const hasMetWith = member && (member.primary_met_with || member.secondary_met_with || member.tertiary_met_with);
    if (hasMetWith) {
      setPendingUnassignMemberId(memberId);
    } else {
      moveMember(memberId, null, null);
    }
  }, [members, moveMember]);

  const confirmUnassign = useCallback(() => {
    if (pendingUnassignMemberId == null) return;
    moveMember(pendingUnassignMemberId, null, null);
    setPendingUnassignMemberId(null);
  }, [pendingUnassignMemberId, moveMember]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // 1. Batch all pending seat assignments
      const results = await Promise.allSettled(
        pendingChanges.map(c => onAssignTable(c.memberId, c.tableId, c.seatPosition))
      );

      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        showError(`Failed to save ${failures.length} assignment(s)`);
      }

      // 2. Salesforce met-with writes, only if all seat assignments succeeded
      if (failures.length === 0) {
        const clearFields = { primary_met_with: null, secondary_met_with: null, tertiary_met_with: null };
        const sfdcPatches: Array<[number, Record<string, unknown>]> = [
          ...pendingChanges.filter(c => c.tableId == null).map((c): [number, Record<string, unknown>] => [c.memberId, clearFields]),
          ...computeMetWith(members, tables, sfdcUsers, effectiveAssignments),
        ];
        const result = await probeThenFanOut(
          sfdcPatches,
          ([memberId, fields]) => onPatchMember(memberId, fields),
          isSalesforceReauthError
        );
        const message = fanOutFailureMessage(result, 'met-with assignment');
        if (message) {
          showError(message);
          return;
        }
        clearPending();
      }
    } finally {
      setSaving(false);
    }
  }, [pendingChanges, onAssignTable, members, tables, sfdcUsers, effectiveAssignments, onPatchMember, clearPending, showError]);

  // Sort tables by sort_order
  const sortedTables = [...tables].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);

  // Set of member IDs with unsaved changes (for pending indicators)
  const pendingMemberIds = useMemo(() => new Set(pending.keys()), [pending]);

  return (
    <div className="flex gap-4" style={{ height: 'calc(100vh - 260px)', minHeight: '400px' }}>
      {/* Left: Unassigned panel + expand tab */}
      <div className="flex-shrink-0 flex h-full">
        <UnassignedPanel
          members={unassignedMembers}
          sfdcUsers={sfdcUsers}
          onDrop={handleUnassignDrop}
          onSelectMember={onSelectMember}
          collapsed={panelCollapsed}
          onToggleCollapse={() => setPanelCollapsed(true)}
        />
        {panelCollapsed && (
          <button
            onClick={() => setPanelCollapsed(false)}
            className="w-7 h-full rounded-lg border border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 hover:border-blueberry-400 dark:hover:border-blueberry-500 flex items-center justify-center text-nightshade-400 hover:text-nightshade-600 transition-colors"
            title="Expand panel"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Right: Table grid — independently scrollable */}
      <div className="flex-1 overflow-y-auto h-full">
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))' }}>
          {sortedTables.map(t => (
            <TableCard
              key={t.id}
              table={t}
              members={tableMembers.get(t.id) ?? []}
              sfdcUsers={sfdcUsers}
              effectiveAssignments={effectiveAssignments}
              pendingMemberIds={pendingMemberIds}
              onMoveMember={moveMember}
              onPatchTable={handlePatchTable}
              onDeleteTable={handleDeleteTable}
              onSelectMember={onSelectMember}
            />
          ))}

          {/* Add table button */}
          <button
            onClick={handleAddTable}
            disabled={createTable.isPending}
            className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-nightshade-200 dark:border-nightshade-700 hover:border-blueberry-400 dark:hover:border-blueberry-500 min-h-[120px] text-nightshade-400 dark:text-nightshade-500 hover:text-blueberry-500 transition-colors disabled:opacity-50"
          >
            <Plus className="w-5 h-5" />
            <span className="text-sm font-medium">Add Table</span>
          </button>
        </div>
      </div>

      {/* Floating save bar */}
      {isDirty && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <button
            onClick={() => { void handleSave(); }}
            disabled={saving}
            className="px-6 py-2.5 rounded-full bg-blueberry-500 hover:bg-blueberry-600 text-white text-sm font-medium shadow-lg shadow-blueberry-500/25 transition-colors disabled:opacity-70 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Saving...
              </>
            ) : (
              `Save ${pendingChanges.length} Change${pendingChanges.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      )}

      {/* Delete table confirmation modal */}
      <Modal
        open={pendingDeleteTableId != null}
        title="Delete Table"
        onClose={() => setPendingDeleteTableId(null)}
        containerClassName="md:!max-w-sm"
        footer={
          <>
            <button
              onClick={() => setPendingDeleteTableId(null)}
              className="px-4 py-2 text-sm rounded-lg border border-nightshade-200 dark:border-nightshade-600 text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800"
            >
              Cancel
            </button>
            <button
              onClick={() => void confirmDeleteTable()}
              className="px-4 py-2 text-sm rounded-lg bg-pomegranate-500 hover:bg-pomegranate-600 text-white font-medium"
            >
              Delete Table
            </button>
          </>
        }
      >
        <p className="text-sm text-nightshade-600 dark:text-nightshade-300">
          {pendingDeleteMembers.length > 0
            ? `This will unassign ${pendingDeleteMembers.length} member${pendingDeleteMembers.length !== 1 ? 's' : ''} from "${pendingDeleteTable?.table_name}" and clear their met-with assignments.`
            : `Delete "${pendingDeleteTable?.table_name}"?`
          }
        </p>
      </Modal>

      {/* Unassign member confirmation modal */}
      <Modal
        open={pendingUnassignMemberId != null}
        title="Unassign Member"
        onClose={() => setPendingUnassignMemberId(null)}
        containerClassName="md:!max-w-sm"
        footer={
          <>
            <button
              onClick={() => setPendingUnassignMemberId(null)}
              className="px-4 py-2 text-sm rounded-lg border border-nightshade-200 dark:border-nightshade-600 text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800"
            >
              Cancel
            </button>
            <button
              onClick={confirmUnassign}
              className="px-4 py-2 text-sm rounded-lg bg-blueberry-500 hover:bg-blueberry-600 text-white font-medium"
            >
              Unassign
            </button>
          </>
        }
      >
        <p className="text-sm text-nightshade-600 dark:text-nightshade-300">
          Unassigning {pendingUnassignMember?.contact_name ?? 'this member'} will clear their met-with assignments when you save.
        </p>
      </Modal>
    </div>
  );
}
